import { Divider } from "@msuite/ui/divider";
import { FormCheckbox, TextInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { type AsyncModalController, ConfirmModal } from "@msuite/ui/modal";
import { CONFIRM_DELETE_INCOMING_INVOICE } from "@msuite/utils/schemas";
import { useForm, useWatch } from "react-hook-form";

/** Props Interface */
interface DeleteIncomingInvoiceModalProps {
	controller: AsyncModalController<string>;
}

/**
 * The confirmation an invoice's deletion asks for, as a project's does: the phrase typed out and
 * the consequences ticked. It resolves with what was typed, which the route checks again.
 */
export function DeleteIncomingInvoiceModal({ controller }: DeleteIncomingInvoiceModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const methods = useForm({
		defaultValues: { confirm: "", iDoUnderstandTheConsequences: false },
	});
	const currentConfirmValue = useWatch({ control: methods.control, name: "confirm" });
	const iDoUnderstandTheConsequences = useWatch({
		control: methods.control,
		name: "iDoUnderstandTheConsequences",
	});

	/** Render */
	return (
		<ConfirmModal
			controller={controller}
			message={t("incoming_invoices.delete_invoice_confirm_message")}
			confirmIsDisabled={
				currentConfirmValue !== CONFIRM_DELETE_INCOMING_INVOICE || !iDoUnderstandTheConsequences
			}
			onResolve={() => {
				const value = methods.getValues("confirm");
				methods.reset();
				return value;
			}}
			data-cy="delete-incoming-invoice-modal"
		>
			<TextInput
				control={methods.control}
				name="confirm"
				label={t("general.confirm")}
				placeholder={CONFIRM_DELETE_INCOMING_INVOICE}
				helperText={t("incoming_invoices.delete_invoice_type_to_confirm", {
					phrase: CONFIRM_DELETE_INCOMING_INVOICE,
				})}
				isRequired
				data-cy="delete-incoming-invoice-confirm-input"
			/>
			<Divider variant="modal" />
			<FormCheckbox
				control={methods.control}
				name="iDoUnderstandTheConsequences"
				data-cy="delete-incoming-invoice-understand-checkbox"
			>
				{t("general.i_do_understand_the_consequences")}
			</FormCheckbox>
		</ConfirmModal>
	);
}
